import type { PointsBreakdownEvent } from './PointsBreakdown';

/**
 * The compact arithmetic line under a breakdown event, and the entries label beside it — pure, so
 * the designer's panel (`PointsBreakdown.tsx`) and player-web's event card say the same words, and
 * so both can be tested without a component runner.
 *
 * <b>Every number here is one the server sent.</b> The line is assembled, never computed: a browser
 * recomputing `base × multiplier` would eventually disagree with the total beside it in the last
 * decimal place, and a breakdown is the one panel where that is the thing being audited.
 */

/**
 * The entries part of the line, in the server sentence's own terms.
 *
 * Counted once per tournament, `participationEntries` is the one entry that earned the value while
 * the sentence beside it says "4 entries that did not cash, counted once" — so the label names the
 * entries the rule **admitted** (`participationEntriesAdmitted`) and says they counted once. Under
 * every other count the two figures are equal and the label is unchanged.
 *
 * `participationEntriesAdmitted` is null on a breakdown stored before the field existed, and absent
 * on a payload from such a build. Both were computed by a build that could not count once, so
 * `participationEntries` is the admitted figure there too.
 */
export function participationEntriesLabel(event: PointsBreakdownEvent): string {
  const counted = event.participationEntries;
  const admitted = event.participationEntriesAdmitted ?? counted;

  return admitted > counted
    ? `${admitted} entries, counted once`
    : `${counted} entr${counted === 1 ? 'y' : 'ies'}`;
}

/**
 * The sum itself, in the engine's own order: base award, then each multiplier, then what the finish
 * was worth; entries beside it; the two added together.
 *
 * <b>A multiplier of exactly 1 is omitted</b>, which is the rule the engine applies when it writes
 * the explanation — "50 × 1.8 = 90.00", never "50 × 1.8 × 1.0 = 90.00". The payload cannot tell "not
 * configured" from "configured and neutral"; to a reader they are the same thing.
 *
 * The sum is stated only when there were two things to add: with one part it would repeat the
 * figure already at the end of the line, and repeated numbers are how a reader starts wondering
 * whether they are different numbers. Null when there is nothing to show.
 */
export function breakdownArithmetic(event: PointsBreakdownEvent, places: number): string | null {
  const parts: string[] = [];

  if (event.place !== null) {
    let finish = number(event.baseAward);
    if (event.fieldMultiplier !== 1) finish += ` × ${number(event.fieldMultiplier)}`;
    if (event.buyInMultiplier !== 1) finish += ` × ${number(event.buyInMultiplier)}`;
    parts.push(`${finish} = ${points(event.scoringResultPoints, places)}`);
  }

  if (event.participationEntries > 0) {
    parts.push(`${participationEntriesLabel(event)} = ${points(event.participationPoints, places)}`);
  }

  if (parts.length === 0) return null;

  return parts.length > 1
    ? `${parts.join('  +  ')}  =  ${points(event.eventScore, places)}`
    : parts[0]!;
}

/**
 * Rounded away from zero, matching the engine — .NET's `MidpointRounding.AwayFromZero`, not
 * JavaScript's default. `toFixed` alone rounds a binary approximation, so a figure the server
 * renders as 90.13 inside its explanation can come out 90.12 in the line above it.
 */
function points(value: number, places: number): string {
  const factor = 10 ** places;
  return ((Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor).toFixed(places);
}

/** A multiplier or an award, without trailing zeros: 1.8 rather than 1.8000. The engine's `0.####`. */
function number(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}
